import { Application, UPDATE_PRIORITY } from 'pixi.js';
import { content, DEFAULT_DUNGEON_ID, DEFAULT_HERO_ID } from '@ml/content';
import { atlasUrl, loadSkin } from './art/skin.ts';
import { setArtResolver } from './board/orbArt.ts';
import { blockBrowserGestures, onViewportChange, viewportSize } from './board/viewport.ts';
import { loadConfig, type LoadedConfig } from './config/loadConfig.ts';
import { GameScene } from './game/gameScene.ts';
import { registerServiceWorker } from './pwa.ts';

declare const __BUILD__: string;

const DEFAULT_SEED = 20261020;

type DebugApi = Record<string, unknown> & {
  ready: boolean;
  scene?: GameScene;
  config?: LoadedConfig;
};

declare global {
  interface Window {
    __ML_DEBUG__?: DebugApi;
  }
}

function seedFromUrl(): number {
  const s = new URLSearchParams(location.search).get('seed');
  const n = s === null ? NaN : Number(s);
  return Number.isFinite(n) ? n >>> 0 : DEFAULT_SEED;
}

function toast(msg: string): void {
  const el = document.createElement('div');
  el.className = 'toast';
  el.textContent = msg;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 6000);
}

async function boot(): Promise<void> {
  const host = document.getElementById('app');
  if (!host) throw new Error('#app not found');
  blockBrowserGestures();
  registerServiceWorker();

  const [loaded, skin] = await Promise.all([loadConfig(), loadSkin()]);
  if (loaded.error) toast(`Không tải được config (${loaded.error}), dùng mặc định`);
  if (skin) setArtResolver((key) => skin.texture(key));

  const vp = viewportSize();
  const app = new Application();
  await app.init({
    width: vp.width,
    height: vp.height,
    background: '#101018',
    antialias: false,
    resolution: Math.min(window.devicePixelRatio || 1, 3),
    autoDensity: true,
    roundPixels: true,
  });
  host.appendChild(app.canvas);

  const scene = new GameScene(app, {
    config: loaded.config,
    content,
    heroId: DEFAULT_HERO_ID,
    dungeonId: DEFAULT_DUNGEON_ID,
    seed: seedFromUrl(),
    skin,
    atlasUrl,
    build: `b${__BUILD__}`,
  });
  onViewportChange(() => scene.relayout());
  // Render theo nhu cầu thay cho render mỗi tick của TickerPlugin.
  app.ticker.remove(app.render, app);
  app.ticker.add(
    () => {
      if (scene.needsRender()) app.render();
    },
    undefined,
    UPDATE_PRIORITY.LOW,
  );

  const dbg: DebugApi = {
    ready: true,
    scene,
    config: loaded,
    build: __BUILD__,
    state: () => scene.debugState(),
    cellCenter: (x: number, y: number) => scene.board.cellCenter(x, y),
    findMove: (len?: number) => scene.board.findMove(len),
    relayout: () => scene.relayout(),
    botStep: () => scene.botStep(),
    botPlayFloor: (max?: number) => scene.botPlayFloor(max),
    chooseBuff: (i: number) => scene.chooseBuff(i),
    restart: () => scene.restart(),
    setHeroHp: (hp: number) => scene.debugSetHeroHp(hp),
    settled: () => scene.settled(),
  };
  // Trường đọc trực tiếp (live getter) để test đọc `__ML_DEBUG__.candidates` v.v.
  for (const k of [
    'board',
    'boardHash',
    'path',
    'candidates',
    'castable',
    'score',
    'turns',
    'moves',
    'animating',
    'layout',
    'phase',
    'floor',
    'round',
    'heroHp',
  ]) {
    Object.defineProperty(dbg, k, { get: () => scene.debugState()[k], enumerable: true });
  }
  window.__ML_DEBUG__ = dbg;
}

void boot();
